import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { UNIT_ROLES, roleColor } from "@/components/people/roles";

/** One bar per unit, cut by role — the strength of every unit team side by side. */
export const TeamStrength = ({ units = [], onPick, filtered, hiddenRoles = [] }) => {
  const rows = units.filter((u) => u.count > 0);
  const max = Math.max(1, ...rows.map((u) => u.count));
  const roles = UNIT_ROLES.filter((r) => !hiddenRoles.includes(r));
  return (
    <div data-testid="team-strength">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="label-caps">Team strength by unit</div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
          {roles.map((r) => (
            <span key={r} className="flex items-center gap-1">
              <span className="h-2 w-2" style={{ backgroundColor: roleColor(r) }} /> {r}
            </span>
          ))}
        </div>
      </div>
      {!rows.length && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="team-strength-empty">
          {filtered ? "No unit team matches these slicers." : "No unit team recorded yet."}
        </p>
      )}
      <ul className="mt-3 space-y-1.5">
        {rows.map((u, i) => (
          <li key={u.plant}>
            <button
              type="button"
              onClick={() => onPick(u.plant)}
              data-testid={`strength-row-${u.plant}`}
              className="group grid w-full grid-cols-[72px_1fr_auto] items-center gap-3 text-left"
              title={roles.map((r) => `${r}: ${u.strength[r] || 0}`).join(" · ")}
            >
              <span className="font-data text-xs font-bold transition-colors duration-200 group-hover:text-primary">
                {u.plant}
              </span>
              <span className="flex h-4 bg-muted" style={{ width: `${(u.count / max) * 100}%` }}>
                {roles.map((r) =>
                  u.strength[r] ? (
                    <motion.span
                      key={r}
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ delay: 0.15 + i * 0.03, duration: 0.45 }}
                      style={{
                        width: `${(u.strength[r] / u.count) * 100}%`,
                        backgroundColor: roleColor(r),
                        transformOrigin: "left",
                      }}
                    />
                  ) : null,
                )}
              </span>
              <span className="flex items-center gap-2 text-[11px]">
                <b className="font-data" data-testid={`strength-count-${u.plant}`}>
                  {u.count}
                </b>
                {!u.strength.SPOC ? (
                  <span
                    data-testid={`strength-no-spoc-${u.plant}`}
                    className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-[hsl(var(--watch))]"
                  >
                    <AlertTriangle className="h-3 w-3" /> No SPOC
                  </span>
                ) : (
                  !u.spoc_on_portal && (
                    <span
                      data-testid={`strength-no-approver-${u.plant}`}
                      title="The SPOC on record has no portal account, so nobody verifies what the unit files"
                      className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-[hsl(var(--watch))]"
                    >
                      <AlertTriangle className="h-3 w-3" /> SPOC not on portal
                    </span>
                  )
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
